import React, { useState, useEffect, useRef, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { PENDING_MESSAGE_ID } from 'core/constants'
import { isNotSentId } from 'core/helpers'
import { useFeatures, useTranslations } from 'core/hooks'
import { Icon, Time } from 'components/misc'
import { ccpProvider } from 'core/CcpProvider'
import CONFIG from 'core/globals'
import { MessageBasic } from './MessageDefault'
import UpgradeLink from '../UpgradeLink'

const formatTime = secs => {
  const s = Math.floor(secs) || 0
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m < 10 ? '0' + m : m}:${r < 10 ? '0' + r : r}`
}

const generatePeaks = url => {
  let v = url.split('').reduce((acc, c) => (Math.imul(acc, 31) + c.charCodeAt(0)) >>> 0, 0)
  return [Array.from({ length: 80 }, () => {
    v = (Math.imul(v, 1664525) + 1013904223) >>> 0
    return (v % 100) / 100
  })]
}

const MessageVoice = ({ message, isCorrespondent, users, user, conversation, conversationUid }) => {
  const waveformRef = useRef(null)
  const [wavesurfer, setWavesurfer] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  // The estimated length from the API, until the real audio is loaded
  const [duration, setDuration] = useState(message.duration)
  const [loading, setLoading] = useState(false)
  // On credit sites the audio is generated on unlock, so the message has no url until then
  const [unlockedUrl, setUnlockedUrl] = useState(null)
  const audioUrl = unlockedUrl || message.message

  let { isCreditSite } = useFeatures()
  if (isCreditSite === 0) { isCreditSite = false }
  const { creditsPerVoiceMessageReveal } = useTranslations()

  const isVoiceLocked = !isCorrespondent &&
    !user.unlockedVoiceMessages.some(id => id === message.id)

  const [isLocked, setIsLocked] = useState(isVoiceLocked)

  const props = {
    time: <Time since={message.sentAt}/>,
    user: isCorrespondent ? users.me : users.other,
    sent: isCorrespondent,
    pending: message.id === PENDING_MESSAGE_ID,
    failed: isNotSentId(message.id),
    shouldShowTimeStamp: message.shouldShowTimeStamp,
  }

  useEffect(() => {
    const audio = document.createElement('audio')
    audio.src = audioUrl
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration))

    const wsOptions = {
      container: waveformRef.current,
      peaks: generatePeaks(String(message.id)),
      duration: message.duration,
      waveColor: 'rgba(104, 115, 121, 0.4)',
      progressColor: '#687379',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 24,
      interact: false,
    }

    wsOptions.media = audio
    if (!isLocked) {
      audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime))
    }

    const ws = WaveSurfer.create(wsOptions)
    ws.on('finish', () => setIsPlaying(false))
    setWavesurfer(ws)

    return () => {
      ws.destroy()
      audio.src = ''
    }
  }, [audioUrl, isLocked])

  const onPlayPause = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause()
      setIsPlaying(!isPlaying)
    }
  }, [wavesurfer, isPlaying])

  const unlock = async e => {
    e.preventDefault()
    if (loading) { return }
    setLoading(true)
    const [result, err] = await ccpProvider.api.user.chargeForVoiceMessageReveal({
      conversationUid,
      url: message.message,
      messageId: message.id,
    })
    if (!err) {
      if (result.url) {
        const stored = conversation.messages.find(m => m.id === message.id)
        if (stored) { stored.message = result.url }
        setUnlockedUrl(result.url)
      }
      user.unlockedVoiceMessages.push(message.id)
      setIsLocked(false)
    } else if (isCreditSite && err.statusCode === 402) {
      CONFIG.hooks.onMessageSend({ showPurchaseModal: true })
    }
    setLoading(false)
  }

  const unlockHtml = creditsPerVoiceMessageReveal || 'Unlock Voice Message'

  return (
    <MessageBasic {...props} type="voice">
      {isLocked ? (
        <>
          <div className="voice-message voice-message--locked">
            <div className="voice-waveform-wrapper">
              <div ref={waveformRef} className="waveform-container"/>
              <div className="voice-lock-icon">
                <Icon iconName="voiceLock"/>
              </div>
            </div>
            <span className="voice-timer">{formatTime(currentTime)}/{formatTime(duration)}</span>
          </div>
          {isCreditSite ? (
            <a
              href="#"
              onClick={loading ? null : unlock}
              className={`voice-unlock-cta${loading ? ' voice-unlock-cta--loading' : ''}`}
              dangerouslySetInnerHTML={{ __html: unlockHtml }}
            />
          ) : (
            <UpgradeLink className="voice-unlock-cta"><span dangerouslySetInnerHTML={{ __html: unlockHtml }}/></UpgradeLink>
          )}
        </>
      ) : (
        <div className="voice-message">
          <div className="voice-play-btn" onClick={onPlayPause}>
            <Icon iconName={isPlaying ? 'pause' : 'play'}/>
          </div>
          <div className="voice-right">
            <div ref={waveformRef} className="waveform-container"/>
            <span className="voice-timer">{formatTime(currentTime)}/{formatTime(duration)}</span>
          </div>
        </div>
      )}
    </MessageBasic>
  )
}

export default MessageVoice
